const elements = new Set();
const pendingCartOperations = () => window.rnShippingCartOperations?.size || 0;
let revision = 0;
let controller;
let queued = false;
const money = (value) => `$${Math.round(value / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;

export function shippingProgress(cart, threshold, allowed, country) {
  if (!Number.isSafeInteger(threshold) || threshold <= 0 || cart.currency !== 'CLP' || country !== 'CL' ||
      !Number.isSafeInteger(cart.total_price) || cart.total_price < 0 || !Array.isArray(cart.items) ||
      !Number.isInteger(cart.item_count) || cart.item_count < 0) return null;
  if (cart.item_count > 0 && (!cart.requires_shipping || !cart.items.length ||
      cart.items.some((item) => !item.requires_shipping || !allowed.has(String(item.variant_id))))) return null;
  const total = cart.total_price;
  const remaining = Math.max(0, threshold - total);
  return {total, remaining, percent: Math.max(0, Math.min(100, total / threshold * 100)),
    state: cart.item_count === 0 ? 'empty' : remaining === 0 ? 'complete' : 'progress'};
}

function invalidate() {
  revision++;
  controller?.abort();
  for (const element of elements) element.hidden = true;
}

function refresh() {
  invalidate();
  if (queued || pendingCartOperations() || !elements.size) return;
  queued = true;
  queueMicrotask(async () => {
    queued = false;
    if (pendingCartOperations() || !elements.size) return;
    const current = revision;
    controller = new AbortController();
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {signal:controller.signal, cache:'no-store', headers:{Accept:'application/json'}});
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (current !== revision || pendingCartOperations()) return;
      for (const element of elements) element.renderCart(cart);
    } catch {
      if (current === revision) for (const element of elements) element.hidden = true;
    }
  });
}

class ShippingProgress extends HTMLElement {
  connectedCallback() {
    elements.add(this);
    refresh();
  }
  disconnectedCallback() {
    elements.delete(this);
  }
  renderCart(cart) {
    const state = shippingProgress(cart, Number(this.dataset.threshold), new Set(this.dataset.allowed.split(',')), this.dataset.country);
    if (!state) { this.hidden = true; return; }
    const copy = JSON.parse(this.querySelector('[data-copy]').textContent);
    const headline = state.state === 'empty' ? copy.empty : state.state === 'complete' ? copy.complete : copy.remaining.replace('__AMOUNT__', money(state.remaining));
    const support = state.state === 'empty' ? copy.minimum.replace('__AMOUNT__', money(Number(this.dataset.threshold))) : state.state === 'complete' ? copy.earned : copy.towards;
    const update = (selector, value) => {
      const node = this.querySelector(selector);
      if (node.textContent !== value) node.textContent = value;
    };
    update('[data-headline]', headline);
    update('[data-support]', support);
    update('[data-total]', money(state.total));
    update('[data-goal]', money(Number(this.dataset.threshold)));
    this.dataset.state = state.state;
    const progress = this.querySelector('[role="progressbar"]');
    progress.setAttribute('aria-valuenow', String(Math.round(state.percent)));
    progress.setAttribute('aria-valuetext', `${money(state.total)} / ${money(Number(this.dataset.threshold))}. ${headline}`);
    this.querySelector('[data-fill]').style.width = `${state.percent}%`;
    this.hidden = false;
  }
}

if (!customElements.get('rn-shipping-progress')) {
  customElements.define('rn-shipping-progress', ShippingProgress);
  for (const name of ['cart:update', 'discount:update', 'cart:error', 'dialog:open', 'shopify:section:load']) document.addEventListener(name, refresh);
  document.addEventListener('rn:cart-operation:start', invalidate);
  document.addEventListener('rn:cart-operation:end', refresh);
  window.addEventListener('pageshow', refresh);
}
